import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";
import useAxios from "../../utils/useAxios"; // Import the custom Axios instance

const AddScript = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    script_name: "",
    script_placement: "Header",
    script: "",
    is_global: "no",
  });
  const axiosAPI = useAxios(); // Use the custom hook for Axios

  // Fetch scripts from the server
  const fetchScripts = async () => {
    try {
      const response = await axiosAPI.get(`/api/scripts/get-scripts`);
      setScripts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching scripts:", error);
      setLoading(false);
    }
  };

  // Fetch scripts on component mount
  useEffect(() => {
    fetchScripts();
  }, []);

  // Reset the form
  const resetForm = () => {
    setFormData({
      id: "",
      script_name: "",
      script_placement: "Header",
      script: "",
      is_global: "no",
    });
  };

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  // Handle form submission (Add or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    if (
      !formData.script_name ||
      !formData.script_placement ||
      !formData.script ||
      !formData.is_global
    ) {
      window.notifyMessage("Error", "All fields are mandatory!", "danger");
      setIsSubmitting(false);
      return;
    }

    try {
      const requestUrl = formData.id
        ? `/api/scripts/update-script/${formData.id}` // Update if ID exists
        : `/api/scripts/add-script`; // Add new script if no ID

      const httpMethod = formData.id ? "PUT" : "POST"; // Use PUT for update, POST for add

      const requestData = { ...formData };

      // Make the API request
      await axiosAPI({
        method: httpMethod,
        url: requestUrl,
        data: requestData,
      });

      window.notifyMessage(
        "Success",
        formData.id
          ? "Script updated successfully"
          : "Script added successfully",
        "success"
      );
      setIsSubmitting(false);
      hideModal("newScriptModal");
      resetForm();
      fetchScripts(); // Reload the scripts
    } catch (error) {
      console.error("Error submitting the form:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while submitting the form.",
        "danger"
      );
      setIsSubmitting(false);
    }
  };

  const openAddModal = () => {
    resetForm();
    showModal("newScriptModal");
  };

  // Open the modal to edit a script
  const openEditModal = (scriptId) => {
    const scriptToEdit = scripts.find((script) => script.id === scriptId);
    if (scriptToEdit) {
      setFormData({
        id: scriptToEdit.id,
        script_name: scriptToEdit.script_name,
        script_placement: scriptToEdit.script_placement,
        script: scriptToEdit.script,
        is_global: scriptToEdit.is_global,
      });
      showModal("newScriptModal");
    }
  };

  const handleDelete = async (scriptId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this script?"
    );
    if (!isConfirmed) return;

    try {
      await axiosAPI.delete(`/api/scripts/delete-script/${scriptId}`);
      window.notifyMessage("Success", "Script deleted successfully", "success");
      fetchScripts();
    } catch (error) {
      console.error("Error deleting script:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting the script.",
        "danger"
      );
    }
  };

  const handleOnOffToggle = async (scriptId, value) => {
    try {
      setScripts((prevScripts) =>
        prevScripts.map((script) =>
          script.id === scriptId ? { ...script, on_off: value } : script
        )
      );

      // Make the API call to update the script's active state
      await axiosAPI.put(`/api/scripts/update-script-onoff/${scriptId}`, {
        on_off: value,
      });

      window.notifyMessage(
        "Success",
        "Script status updated successfully.",
        "success"
      );
    } catch (error) {
      console.error("Error updating script status:", error);
      window.notifyMessage(
        "Error",
        "Failed to update script status.",
        "danger"
      );
    }
  };

  // Show modal
  const showModal = (id) => {
    const modalEl = document.querySelector(`#${id}`);
    const modalObj =
      window.bootstrap.Modal.getInstance(modalEl) ||
      new window.bootstrap.Modal(modalEl, { backdrop: "static" });
    modalObj.show();
  };

  // Hide modal
  const hideModal = (id) => {
    const modalEl = document.querySelector(`#${id}`);
    const modalObj = window.bootstrap.Modal.getInstance(modalEl);
    if (modalObj) modalObj.hide();
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-dark btn-round mb-4"
            onClick={() => openAddModal()}
          >
            <i className="far fa-plus-square me-2"></i> New Script
          </button>
        </div>

        {/* Modal for Adding/Editing Script */}
        <div
          className="modal fade"
          id="newScriptModal"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {formData.id ? "Edit Script" : "Add New Script"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => hideModal("newScriptModal")}
                ></button>
              </div>
              <div className="modal-body bg-light">
                <form onSubmit={handleSubmit}>
                  <div className="card">
                    <div className="card-body">
                      <div className="row mt-3">
                        <div className="col-md-12 mb-3">
                          <label htmlFor="script_name" className="form-label">
                            Script Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="script_name"
                            value={formData.script_name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="col-md-12 mb-3">
                          <label
                            htmlFor="script_placement"
                            className="form-label"
                          >
                            Script Placement
                          </label>
                          <select
                            className="form-control"
                            id="script_placement"
                            value={formData.script_placement}
                            onChange={handleChange}
                            required
                          >
                            <option value="header">Header</option>
                            <option value="footer">Footer</option>
                          </select>
                        </div>
                        <div className="col-md-12 mb-3">
                          <label htmlFor="script" className="form-label">
                            Script
                          </label>
                          <textarea
                            className="form-control"
                            id="script"
                            rows="5"
                            value={formData.script}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-12 mb-3">
                          <label htmlFor="is_global" className="form-label">
                            Global
                          </label>
                          <select
                            className="form-control"
                            id="is_global"
                            value={formData.is_global}
                            onChange={handleChange}
                            required
                          >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : formData.id ? (
                      "Save Script"
                    ) : (
                      "Add Script"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Scripts Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">All Scripts ({scripts.length})</h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Script Name</th>
                        <th>ON/OFF</th>
                        <th>Placement</th>
                        <th>Global</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : (
                        scripts.map((script) => (
                          <tr key={script.id}>
                            <td>{script.script_name}</td>
                            <td>
                              <div className="form-switch p-0">
                                <input
                                  className="form-check-input fs-5 m-0"
                                  type="checkbox"
                                  role="switch"
                                  id={`switchChecked-${script.id}`}
                                  checked={script.on_off === "on"}
                                  onChange={(e) =>
                                    handleOnOffToggle(
                                      script.id,
                                      e.target.checked ? "on" : "off"
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td>{script.script_placement}</td>
                            <td>{script.is_global}</td>
                            <td>
                              <button
                                className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                onClick={() => openEditModal(script.id)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                onClick={() => handleDelete(script.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddScript;
